import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import BrainConsole from "@/components/BrainConsole";
import VitalsPanel from "@/components/VitalsPanel";
import DockingBay from "@/components/DockingBay";
import CameraVision from "@/components/CameraVision";
import PeopleMemory from "@/components/PeopleMemory";
import IntentGaps from "@/components/IntentGaps";
import GenomeCore from "@/components/GenomeCore";
import PrefrontalCortex from "@/components/PrefrontalCortex";
import AIFrontierRadar from "@/components/AIFrontierRadar";
import Neocortex from "@/components/Neocortex";
import { useVoiceSession } from "@/context/VoiceSessionContext";

export default function OSShell() {
  const location = useLocation();
  const [conversations, setConversations] = useState([]);

  // Morning briefing (visual): on Doc's first OS visit of the day, surface the
  // overnight sleep report unprompted - the voice orb delivers the spoken version.
  useEffect(() => {
    try {
      const today = new Date().toDateString();
      if (localStorage.getItem("ava_briefing_visual_date") === today) return;
      localStorage.setItem("ava_briefing_visual_date", today);
      api.get("/brain/sleep-report").then((res) => {
        if (res.data?.narration) {
          toast("Overnight briefing from AVA", { description: res.data.narration, duration: 15000 });
        }
      }).catch(() => {});
    } catch (e) { /* localStorage unavailable */ }
  }, []);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState(location.state?.openDockingBay ? "docking" : "console");
  const [focusProjectId, setFocusProjectId] = useState(location.state?.focusProjectId || null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  // Mobile-only sidebar drawer state. On desktop the sidebar is always visible in-grid;
  // on mobile (< md) it becomes a slide-in drawer to reclaim the full width for content.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { setActiveConversationId, setActiveMode, registerConversationListener } = useVoiceSession();

  const fetchConversations = useCallback(async () => {
    const res = await api.get("/conversations");
    setConversations(res.data);
    return res.data;
  }, []);

  useEffect(() => {
    fetchConversations().then((data) => {
      if (data.length > 0) setActiveId(data[0].id);
    });
    registerConversationListener((id) => {
      fetchConversations().then(() => setActiveId(id));
    });
  }, [fetchConversations, registerConversationListener]);

  const handleNew = async () => {
    const res = await api.post("/conversations", { mode: "balanced" });
    setConversations((prev) => [res.data, ...prev]);
    setActiveId(res.data.id);
    setView("console");
    setMobileSidebarOpen(false);
  };

  const handleDelete = async (id) => {
    await api.delete(`/conversations/${id}`);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeId === id) setActiveId(null);
  };

  const handleModeChange = async (mode) => {
    if (!activeId) return;
    await api.put(`/conversations/${activeId}`, { mode }).catch(() => {});
    setConversations((prev) => prev.map((c) => (c.id === activeId ? { ...c, mode } : c)));
  };

  const activeConversation = conversations.find((c) => c.id === activeId);

  useEffect(() => {
    setActiveConversationId(activeId || null);
    setActiveMode(activeConversation?.mode || "balanced");
  }, [activeId, activeConversation, setActiveConversationId, setActiveMode]);

  const handleSelectConversation = (id) => {
    setActiveId(id);
    setView("console");
    setMobileSidebarOpen(false);
  };

  // Allow child panels (e.g. the compact graph's "expand" button) to jump views.
  useEffect(() => {
    const h = (e) => { if (e?.detail) { setView(e.detail); setMobileSidebarOpen(false); } };
    window.addEventListener("ava:navigate", h);
    return () => window.removeEventListener("ava:navigate", h);
  }, []);

  const handleViewChange = (v) => {
    setView(v);
    setMobileSidebarOpen(false);
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#030509] grid-texture p-2 sm:p-4">
      {/* Mobile top bar - hamburger + current-view label + placeholder for balance. Only
          visible on small screens; desktop keeps the in-grid Sidebar so this row hides. */}
      <div className="md:hidden flex items-center justify-between mb-2 px-1" data-testid="os-mobile-topbar">
        <button
          data-testid="os-mobile-menu-toggle"
          onClick={() => setMobileSidebarOpen(true)}
          className="p-2 rounded-lg bg-white/5 border border-white/10 text-cyan-300 hover:bg-white/10 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-4 h-4" />
        </button>
        <span className="text-[10px] font-mono-data uppercase tracking-[0.25em] text-cyan-300">
          {view === "console" ? "Brain Console" : view === "docking" ? "Docking Bay" : view === "vision" ? "Vision" : view === "gaps" ? "Intent Gaps" : view === "core" ? "OCTON Core" : view === "prefrontal" ? "Prefrontal Cortex" : view === "radar" ? "AI Frontier Radar" : view === "neocortex" ? "Neocortex" : "People Memory"}
        </span>
        <span className="w-8" />
      </div>

      {/* Mobile drawer + backdrop for the Sidebar. Slides in from the left with a spring
          feel; tapping the backdrop or picking any nav item closes it automatically. */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <>
            <motion.div
              key="os-mobile-backdrop"
              data-testid="os-mobile-sidebar-backdrop"
              className="md:hidden fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileSidebarOpen(false)}
            />
            <motion.div
              key="os-mobile-drawer"
              data-testid="os-mobile-sidebar-drawer"
              className="md:hidden fixed inset-y-0 left-0 z-[71] w-[82%] max-w-[320px] flex flex-col p-3"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
            >
              <button
                data-testid="os-mobile-menu-close"
                onClick={() => setMobileSidebarOpen(false)}
                className="self-end mb-2 p-2 rounded-lg bg-white/5 border border-white/10 text-slate-300 hover:text-white transition-colors"
                aria-label="Close menu"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="grid grid-cols-1 flex-1 min-h-0">
                <Sidebar
                  conversations={conversations}
                  activeId={activeId}
                  onSelect={handleSelectConversation}
                  onNew={handleNew}
                  onDelete={handleDelete}
                  view={view}
                  onViewChange={handleViewChange}
                  mobileVariant
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 sm:gap-4 h-full min-h-0">
        <div className="hidden md:contents">
          <Sidebar
            conversations={conversations}
            activeId={activeId}
            onSelect={handleSelectConversation}
            onNew={handleNew}
            onDelete={handleDelete}
            view={view}
            onViewChange={handleViewChange}
          />
        </div>
        {view === "console" ? (
          <>
            <BrainConsole
              conversation={activeConversation}
              onModeChange={handleModeChange}
              onExchangeComplete={() => setRefreshSignal((s) => s + 1)}
            />
            <VitalsPanel refreshSignal={refreshSignal} />
          </>
        ) : view === "docking" ? (
          <DockingBay focusProjectId={focusProjectId} onFocusHandled={() => setFocusProjectId(null)} />
        ) : view === "vision" ? (
          <CameraVision />
        ) : view === "gaps" ? (
          <IntentGaps />
        ) : view === "core" ? (
          <GenomeCore />
        ) : view === "prefrontal" ? (
          <PrefrontalCortex />
        ) : view === "radar" ? (
          <AIFrontierRadar />
        ) : view === "neocortex" ? (
          <Neocortex />
        ) : (
          <PeopleMemory />
        )}
      </div>
    </div>
  );
}
